import React, { useEffect, useState } from "react";
import { Box, Button, List, ListItem, Tooltip, Typography } from "@mui/material";
import Draggable from "react-draggable";
import { useDispatch } from "react-redux";
import { setSelectedGroup } from "redux/MapSlice";
import asset from "json/assets";
import events from "json/events";
import eventEmitter from "services/event.emitter";
import { useEventEmitter } from "hooks/useEventEmitter";
import { useCourseGPSMappingData, useTransformedHoleData } from "hooks/CourseMapDetails";
import popOverStyles from "styles/component/CustomCoursePopOver.module.scss";
import styles from "styles/component/ToolsMenu.module.scss";
import FlipNinesModal from "./FlipNinesModal";
import DuplicateBackNineModal from "./DuplicateBackNineModal";
import { evaluateDuplicatePreconditions } from "./duplicateBackNine";

const TOOLS_POPOVER_TITLE = "Tools";

const ToolsMenu = ({ layoutHoles }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [flipOpen, setFlipOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const holeWiseData = useTransformedHoleData();
  const courseGPSMapping = useCourseGPSMappingData();

  useEventEmitter(events.openToolsMenu, (e) => {
    const visible = e?.visible ?? true;
    setOpen(visible);
    if (visible) {
      eventEmitter.emit(events.openMapElementInfoModal, {
        visible: true,
        title: TOOLS_POPOVER_TITLE,
      });
      eventEmitter.emit(events.addMarkerToggle, { visible: false });
      dispatch(setSelectedGroup(""));
    }
  });

  useEventEmitter(events.openMapElementInfoModal, (e) => {
    if (e?.visible && e?.title !== TOOLS_POPOVER_TITLE) {
      setFlipOpen(false);
      setDuplicateOpen(false);
      setOpen(false);
    }
  });

  useEffect(() => {
    eventEmitter.emit(events.toolsMenuVisibility, { visible: open });
  }, [open]);

  const canFlip = Number(layoutHoles) === 18;
  const duplicateCheck = open
    ? evaluateDuplicatePreconditions({
        layoutHoles,
        holeWiseData,
        gpsList: courseGPSMapping?.GPSList,
      })
    : { canDuplicate: false, reason: "" };

  const handleClose = () => {
    setFlipOpen(false);
    setDuplicateOpen(false);
    setOpen(false);
  };

  const handleImageOverlay = () => {
    eventEmitter.emit(events.openImageOverlayPanel, { visible: true });
    setOpen(false);
  };

  const handleFullCourseShift = () => {
    eventEmitter.emit(events.openFullCourseShift, { visible: true });
    setOpen(false);
  };

  const handleFlipNines = () => {
    setFlipOpen(true);
    setOpen(false);
  };

  const handleDuplicateBackNine = () => {
    setDuplicateOpen(true);
    setOpen(false);
  };

  return (
    <>
      {open && (
        <Draggable
          bounds={"#DraggableContainer"}
          defaultPosition={{ x: 0, y: 0 }}
          cancel=".MuiSlider-root, button, input"
        >
          <Box
            className={`${popOverStyles.poover_content} ${popOverStyles.default_poover_content} ${styles.tools_panel} custom_popover_content`}
          >
            <Box className={popOverStyles.poover_head}>
              <Typography variant="h5">Tools</Typography>
              <List>
                <ListItem>
                  <Tooltip title="Close" arrow placement="top">
                    <Button onClick={handleClose}>
                      <img src={asset.close} alt="" />
                    </Button>
                  </Tooltip>
                </ListItem>
              </List>
            </Box>
            <Box className={`${popOverStyles.popover_body} ${styles.panel_body}`}>
              <Tooltip
                title={canFlip ? "" : "Course must have 18 holes"}
                arrow
                placement="top"
              >
                <span className={styles.tool_btn_wrap}>
                  <Button
                    className={styles.tool_btn}
                    disabled={!canFlip}
                    onClick={handleFlipNines}
                  >
                    FLIP 9'S
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={duplicateCheck.canDuplicate ? "" : duplicateCheck.reason}
                arrow
                placement="top"
              >
                <span className={styles.tool_btn_wrap}>
                  <Button
                    className={styles.tool_btn}
                    disabled={!duplicateCheck.canDuplicate}
                    onClick={handleDuplicateBackNine}
                  >
                    DUPLICATE TO BACK 9
                  </Button>
                </span>
              </Tooltip>
              <span className={styles.tool_btn_wrap}>
                <Button
                  className={styles.tool_btn}
                  onClick={handleFullCourseShift}
                >
                  FULL COURSE SHIFT
                </Button>
              </span>
              <span className={styles.tool_btn_wrap}>
                <Button className={styles.tool_btn} onClick={handleImageOverlay}>
                  IMAGE OVERLAY
                </Button>
              </span>
            </Box>
          </Box>
        </Draggable>
      )}
      {flipOpen && (
        <FlipNinesModal
          layoutHoles={layoutHoles}
          onClose={() => setFlipOpen(false)}
          onSuccess={handleClose}
        />
      )}
      {duplicateOpen && (
        <DuplicateBackNineModal
          onClose={() => setDuplicateOpen(false)}
          onSuccess={handleClose}
        />
      )}
    </>
  );
};

export default ToolsMenu;
